import React, { useCallback, useEffect, useState } from "react";
import baseUrl from "../../../../../api/baseUrl";
import {
  customer,
  customerData,
  customerName,
  focusInput,
} from "../../../../../store/pos/posAction";
import { useDispatch, useSelector } from "react-redux";
import "./CustomerModel.scss";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  posCommonChange,
  posInputFocus,
} from "../../../../../store/pos/posSlice";
import { selectCustomer } from "../../../../../store/pos/posSlice";
import DropInput from "../../../../../components/inputs/DropInput";
import InputText from "../../../../../components/inputs/InputText";
import PhoneNumberInput from "../../../../../components/inputs/PhoneNumberInput";
import useSubmitting from "../../../../../hooks/useSubmitting";
import { getDecryptedToken } from "../../../../../utils/crypto/cryptoUtils";

const CustomerModel = ({
  isCloseMain,
  isOpen,
}: {
  isCloseMain: () => void;
  isOpen: boolean;
}) => {
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customersFatch, setCustomers] = useState<any[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, wrap] = useSubmitting();

  const item = useSelector((state: any) => state.pos.current);
  const dispatch = useDispatch();

  const fetchData = useCallback(async () => {
    try {
      const res = await baseUrl.get("/branch/customer/get-customers");
      const customers = res.data.data || [];
      setCustomers(customers);
      setFilteredCustomers(customers);
    } catch (err) {
      console.log(err);
    }
  }, []);

  const [tableData, setTableData] = useState<any[]>([]);
  const fetchRoles = useCallback(async () => {
    try {
      const res = await baseUrl.get("branch/loyalty/get-loyalties");
      let loyaltyList = res.data.data || [];

      if (loyaltyList.length === 0) {
        const martId = getDecryptedToken("martId");
        try {
          const createRes = await baseUrl.post(
            "/branch/loyalty/create-loyalty",
            {
              loyalty: "General",
              martId: parseInt(martId || "0"),
            },
          );
          loyaltyList = [createRes.data.data];
        } catch {
          const refetch = await baseUrl.get("branch/loyalty/get-loyalties");
          loyaltyList = refetch.data.data || [];
        }
      }

      setTableData(loyaltyList);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchData();
  }, [fetchData, fetchRoles]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedCustomer(null);
      setSearchQuery("");
      setShowAddForm(false);
      setFilteredCustomers(customersFatch);
      // Reset form
      dispatch(posCommonChange({ name: "customerName", value: "" }));
      dispatch(posCommonChange({ name: "phoneNumber", value: "" }));
      dispatch(posCommonChange({ name: "loyaltyId", value: "" }));
      dispatch(posCommonChange({ name: "customerType", value: "" }));
    } else {
      // Set defaults when modal opens
      if (tableData.length > 0) {
        dispatch(
          posCommonChange({
            name: "loyaltyId",
            value: String(tableData[0].id),
          }),
        );
      }
      dispatch(posCommonChange({ name: "customerType", value: "normal" }));
    }
  }, [isOpen, customersFatch, dispatch, tableData]);

  // Handle search functionality
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredCustomers(customersFatch);
      setShowAddForm(false);
    } else {
      const filtered = customersFatch.filter(
        (customer: any) =>
          customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          customer.phoneNumber?.includes(searchQuery),
      );
      setFilteredCustomers(filtered);

      // Show add form if no customers found and search query is not empty
      setShowAddForm(filtered.length === 0 && searchQuery.trim() !== "");

      // Pre-fill form with search query
      if (filtered.length === 0 && searchQuery.trim() !== "") {
        // If search query looks like a phone number (digits only or starts with +)
        if (/^[+\d\s\-()]+$/.test(searchQuery)) {
          dispatch(
            posCommonChange({ name: "phoneNumber", value: searchQuery }),
          );
          dispatch(posCommonChange({ name: "customerName", value: "" }));
        } else {
          // Otherwise treat as name
          dispatch(
            posCommonChange({ name: "customerName", value: searchQuery }),
          );
          dispatch(posCommonChange({ name: "phoneNumber", value: "" }));
        }
      }
    }
  }, [searchQuery, customersFatch, dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Debug: log the item state to check phoneNumber
    const data = {
      name: item.customerName,
      phoneNumber: item.phoneNumber,
      loyaltyId: item.loyaltyId,
      customerType: item.customerType,
    };

    try {
      const res = await baseUrl.post("/branch/customer/create-customer", data);
      console.log(res);
      await fetchData();
      setShowAddForm(false);
      setSearchQuery("");
      // Reset form
      dispatch(posCommonChange({ name: "customerName", value: "" }));
      dispatch(posCommonChange({ name: "phoneNumber", value: "" }));
      dispatch(posCommonChange({ name: "loyaltyId", value: "" }));
      dispatch(posCommonChange({ name: "customerType", value: "" }));
    } catch (err) {
      console.log(err);
    } finally {
      // submitting state handled by useSubmitting wrapper
    }
  };

  const handleSelectCustomer = (customer: any) => {
    // Dispatch customer selection to Redux store
    dispatch(selectCustomer(customer));
    isCloseMain();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatch(posCommonChange({ name: name, value: value }));
  };

  const handlePhoneChange = (value: any) => {
    // Support both string and event-like object
    let phoneValue = "";
    if (typeof value === "string") {
      phoneValue = value;
    } else if (
      value &&
      value.target &&
      typeof value.target.value === "string"
    ) {
      phoneValue = value.target.value;
    }
    dispatch(posCommonChange({ name: "phoneNumber", value: phoneValue }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Handle click outside modal to close
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      isCloseMain();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pos_modal_overlay" onClick={handleOverlayClick}>
      <div className="pos_modal_container customer_modal">
        {/* Modal Header */}
        <div className="pos_modal_header">
          <h2 className="pos_modal_title">Customer Management</h2>
          <button className="pos_modal_close" onClick={isCloseMain}>
            <CloseIcon />
          </button>
        </div>

        {/* Fixed Search Bar */}
        <div className="search_container_fixed">
          <div className="search_input_wrapper">
            <SearchIcon className="search_icon" />
            <input
              type="text"
              className="search_input"
              placeholder="Search by name or phone number..."
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
            />
          </div>
        </div>

        {/* Scrollable Modal Body */}
        <div className="pos_modal_body scrollable">
          {/* Add Customer Form - Show when no results found */}
          {showAddForm && (
            <div className="add_customer_section">
              <div className="add_customer_header">
                <PersonAddIcon />
                <h3>Customer not found. Add new customer:</h3>
              </div>
              <div className="pos_modal_form">
                <div className="form_grid">
                  <div className="form_group">
                    <InputText
                      required={true}
                      name="customerName"
                      label="Customer Name"
                      type="text"
                      placeholder="Enter customer name"
                      value={item.customerName || ""}
                      onChange={handleInputChange}
                      onFocus={(e) => {
                        dispatch(
                          posInputFocus({ name: "data", value: e.target.name }),
                        );
                      }}
                    />
                  </div>
                  <div className="form_group">
                    <PhoneNumberInput
                      required
                      value={item.phoneNumber || ""}
                      onChange={handlePhoneChange}
                      label="Phone Number"
                      name="phoneNumber"
                      placeholder="Enter phone number with country code"
                    />
                  </div>
                  <div className="form_group">
                    <DropInput
                      required={true}
                      onChange={handleInputChange}
                      name="loyaltyId"
                      label="Loyalty Program"
                      value={item.loyaltyId || ""}
                      option={tableData}
                      dname="loyalty"
                      dvalue="id"
                    />
                  </div>
                  <div className="form_group">
                    <DropInput
                      required={true}
                      onChange={handleInputChange}
                      name="customerType"
                      label="Customer Type"
                      value={item.customerType || ""}
                      option={[
                        { name: "VIP", value: "vip" },
                        { name: "Normal", value: "normal" },
                      ]}
                    />
                  </div>
                </div>

                <div className="add_form_footer">
                  <button
                    type="button"
                    className="btn_cancel"
                    onClick={() => {
                      setShowAddForm(false);
                      setSearchQuery("");
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn_submit"
                    onClick={wrap(handleSubmit)}
                    disabled={submitting}
                  >
                    {submitting ? "Adding..." : "Add Customer"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Customer List */}
          <div className="customer_list_view">
            <div className="customers_container">
              {filteredCustomers?.length > 0 ? (
                filteredCustomers.map((customer: any) => (
                  <div
                    key={customer.id}
                    className={`customer_item ${
                      selectedCustomer?.id === customer.id ? "selected" : ""
                    }`}
                    onClick={() => setSelectedCustomer(customer)}
                  >
                    <div className="customer_info">
                      <div className="customer_name">
                        <PersonIcon />
                        {customer.name}
                      </div>
                      <div className="customer_phone">
                        {customer.phoneNumber}
                      </div>
                      <div className="customer_type">
                        {customer.customerType}
                      </div>
                    </div>
                    <button
                      className="btn_select"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectCustomer(customer);
                      }}
                    >
                      Select
                    </button>
                  </div>
                ))
              ) : searchQuery === "" ? (
                <div className="empty_state">
                  <PersonIcon />
                  <p>Start typing to search customers...</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerModel;
